import { useNavigate } from "@tanstack/react-router";
import { cx } from "@/utils/cx";
import { ViewTransitionTypes, viewTransition } from "@/utils/view-transitions";

const LOGO_MINIMAL = "/images/LogoMinimal.png";

interface NavHeaderProps {
	/** Logo image URL for the expanded state (e.g. AdminLogo.png / LogoFull@2x.png). */
	logoSrc: string;
	/** Accessible alt text for the logo. */
	logoAlt: string;
	/** Optional class overrides for the expanded logo image sizing. */
	logoClassName?: string;
	/**
	 * Icon-only mark for the collapsed sidebar.
	 * Defaults to LogoMinimal.png (OpSource mark from LogoFull).
	 */
	logoIconSrc?: string;
	/**
	 * Collapsed Navigation state — shows LogoMinimal centered
	 * instead of the full wordmark lockup. Keeps the same vertical
	 * spacing-4xl band as expanded so the mark does not jump.
	 */
	isCollapsed?: boolean;
	/**
	 * Home route for this app shell (application Home, org-admin overview, or
	 * platform admin overview). Logo click navigates here.
	 */
	homeUrl?: string;
	/** Accessible label for the logo home control. */
	homeAriaLabel?: string;
	/** Additional classes on the header root. */
	className?: string;
}

/**
 * Brand sidebar logo header (Figma Nav Header — node 3177:5829).
 *
 * Expanded: padding `spacing-4xl` (32px), full logo lockup (40px tall).
 * Collapsed: same vertical padding/band; LogoMinimal mark at 34×30.
 * Clicking the logo navigates to `homeUrl` when provided.
 */
export function NavHeader({
	logoSrc,
	logoAlt,
	logoClassName,
	logoIconSrc = LOGO_MINIMAL,
	isCollapsed = false,
	homeUrl,
	homeAriaLabel,
	className,
}: NavHeaderProps) {
	const navigate = useNavigate();

	const logoImage = isCollapsed ? (
		<img
			src={logoIconSrc}
			alt={logoAlt}
			/* LogoFull mark crop: 34×30 */
			className="h-[30px] w-[34px] object-contain"
		/>
	) : (
		<img
			src={logoSrc}
			alt={logoAlt}
			className={cx(
				"h-10 w-[163px] object-contain object-left shrink-0",
				logoClassName,
			)}
		/>
	);

	return (
		<div
			className={cx(
				"flex flex-col self-stretch",
				/* Vertical band matches expanded (spacing-4xl). Horizontal
				 * spacing-4xl only when expanded — collapsed 88px rail cannot
				 * afford 32px side padding without shrinking the mark. */
				isCollapsed
					? "items-center justify-center py-[var(--spacing-4xl,32px)] px-2"
					: "items-start p-[var(--spacing-4xl,32px)]",
				className,
			)}
		>
			{/* Fixed 40px slot (h-10) — same in both states so vertical position is stable */}
			<div
				className={cx(
					"flex h-10 shrink-0 items-center",
					isCollapsed ? "w-full justify-center" : "w-full justify-start",
				)}
			>
				{homeUrl ? (
					<button
						type="button"
						aria-label={homeAriaLabel ?? logoAlt}
						onClick={() =>
							navigate({
								to: homeUrl,
								...viewTransition(ViewTransitionTypes.FADE),
							})
						}
						className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer bg-transparent border-0 p-0"
					>
						{logoImage}
					</button>
				) : (
					logoImage
				)}
			</div>
		</div>
	);
}
